import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
  Select as AriaSelect,
  type SelectProps as AriaSelectProps,
  Button,
  composeRenderProps,
  Header,
  Label,
  ListBox,
  ListBoxItem,
  type ListBoxItemProps,
  ListBoxSection,
  Popover,
  SelectValue,
} from "react-aria-components";
import { tv } from "tailwind-variants";
import { focusRing } from "./primitive";

// ---------------------------------------------------------------------------
// Styles
// ---------------------------------------------------------------------------

const selectTriggerStyles = tv({
  extend: focusRing,
  base: [
    "flex h-9 w-full cursor-pointer items-center justify-between gap-2",
    "rounded-lg border border-border bg-bg px-3 text-sm text-fg",
    "transition-colors",
    "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
  ],
  variants: {
    isOpen: {
      true: "border-primary ring-1 ring-primary",
    },
    size: {
      small: "h-8 px-2.5 text-xs",
      medium: "h-9 px-3 text-sm",
    },
  },
  defaultVariants: {
    size: "medium",
  },
});

const listBoxStyles = tv({
  base: "max-h-60 w-[var(--trigger-width)] overflow-auto rounded-lg border border-border bg-overlay p-1 shadow-lg outline-none",
});

const listBoxItemStyles = tv({
  base: [
    "flex cursor-pointer items-center rounded-md px-2.5 py-1.5 text-sm text-overlay-fg outline-none transition-colors",
    "data-[hovered]:bg-secondary data-[hovered]:text-secondary-fg",
    "data-[focused]:bg-secondary data-[focused]:text-secondary-fg",
    "data-[selected]:bg-primary data-[selected]:font-medium data-[selected]:text-primary-fg",
    "data-[disabled]:cursor-not-allowed data-[disabled]:opacity-50",
  ],
});

const sectionHeaderStyles = tv({
  base: "px-2.5 pt-2 pb-0.5 text-[10px] font-semibold uppercase tracking-wider text-muted-fg first:pt-1",
});

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface SelectProps<T extends object>
  extends Omit<AriaSelectProps<T>, "children"> {
  label?: string;
  items?: Iterable<T>;
  children: React.ReactNode | ((item: T) => React.ReactNode);
  size?: "small" | "medium";
  popoverClassName?: string;
}

export interface SelectSectionProps extends React.PropsWithChildren {
  title: string;
  className?: string;
}

export interface SelectItemProps extends ListBoxItemProps {
  children: React.ReactNode;
}

// ---------------------------------------------------------------------------
// Components
// ---------------------------------------------------------------------------

export function Select<T extends object>({
  label,
  items,
  children,
  className,
  size,
  popoverClassName,
  ...props
}: SelectProps<T>) {
  return (
    <AriaSelect
      className={composeRenderProps(
        className,
        (className) => `flex flex-col gap-1.5 ${className ?? ""}`,
      )}
      {...props}
    >
      {label && <Label className="text-xs font-medium text-muted-fg">{label}</Label>}
      <Button className={(renderProps) => selectTriggerStyles({ ...renderProps, size })}>
        <SelectValue className="flex-1 truncate text-left data-[placeholder]:text-muted-fg" />
        <ChevronDownIcon className="size-4 shrink-0 text-muted-fg" />
      </Button>
      <Popover
        className={popoverClassName ?? "w-[var(--trigger-width)] entering:animate-in entering:fade-in exiting:animate-out exiting:fade-out"}
        offset={4}
      >
        {items ? (
          <ListBox items={items} className={listBoxStyles()}>
            {children as (item: T) => React.ReactNode}
          </ListBox>
        ) : (
          <ListBox className={listBoxStyles()}>
            {children as React.ReactNode}
          </ListBox>
        )}
      </Popover>
    </AriaSelect>
  );
}

export function SelectItem({ children, ...props }: SelectItemProps) {
  return (
    <ListBoxItem
      className={composeRenderProps(props.className, (className) =>
        listBoxItemStyles({ className }),
      )}
      {...props}
    >
      {children}
    </ListBoxItem>
  );
}

export function SelectSection({ title, children, className }: SelectSectionProps) {
  return (
    <ListBoxSection className={className}>
      <Header className={sectionHeaderStyles()}>{title}</Header>
      {children}
    </ListBoxSection>
  );
}
